import { useState, useCallback, useContext } from 'react';
import React from 'react';
import { AppContext, appContext } from '../../../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { ImportedProduct, UnprocessableError } from '@anticatrade/shared';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';

const DB_NAME = 'anticatrade';
const STORE_NAME = 'imports';
const IMPORT_KEY = 'imported_product';

function openImportDB(): Promise<IDBDatabase> {
	return new Promise((resolve, reject) => {
		const request = indexedDB.open(DB_NAME, 1);
		request.onupgradeneeded = () => {
			// `onupgradeneeded` is the callback executed by indexed
			// DB on first access (typically used for DB creation)
			const db = request.result;
			if (!db.objectStoreNames.contains(STORE_NAME)) {
				db.createObjectStore(STORE_NAME);
			}
		};
		request.onsuccess = () => resolve(request.result);
		request.onerror = () => reject(request.error);
	});
}

const saveImportedProduct = async (data: ImportedProduct): Promise<void> => {
	const db = await openImportDB();
	return await new Promise((resolve, reject) => {
		const tx = db.transaction(STORE_NAME, 'readwrite');
		tx.objectStore(STORE_NAME).put(data, IMPORT_KEY);
		tx.oncomplete = () => resolve();
		tx.onerror = () => reject(tx.error);
	});
}

const consumeImportedProduct = async (): Promise<ImportedProduct | null> => {
	const db = await openImportDB();
	return await new Promise((resolve, reject) => {
		const tx = db.transaction(STORE_NAME, 'readwrite');
		const store = tx.objectStore(STORE_NAME);
		const getReq = store.get(IMPORT_KEY);
		getReq.onsuccess = () => {
			const result = getReq.result as ImportedProduct | undefined;
			if (result != null) {
				store.delete(IMPORT_KEY);
			}
			tx.oncomplete = () => resolve(result ?? null);
		};
		tx.onerror = () => reject(tx.error);
	});
}

function useImportProduct() {
	const [url, setUrl] = useState('');
	const [platform, setPlatform] = useState('proantic');
	const [isImporting, setIsImporting] = useState(false);
	const [error, setError] = useState<string>();

	const { api, handleError } = useContext(appContext as React.Context<AppContext>);
	const { t } = useTranslation();
	const navigate = useNavigate();

	const onImport = useCallback(async () => {
		if (!url.trim()) {
			return;
		}
		setError(undefined);
		setIsImporting(true);
		try {
			const res = await api.importProduct(url.trim(), platform);
			const imported: ImportedProduct = res.import;
			try {
				await saveImportedProduct(imported);
			} catch (err) {
				Sentry.captureException(err);
				setError(t('Something went wrong, please try again and if the issue persists contact our support'));
				setIsImporting(false);
				return;
			}
			setIsImporting(false);
			navigate('/products/add?import=true');
		} catch (err) {
			setIsImporting(false);
			if (err instanceof UnprocessableError) {
				setError(t(err.code));
			} else {
				handleError(err as Error);
			}
		}
	}, [url, platform, api, navigate, handleError, t]);

	return {
		url,
		setUrl,
		platform,
		setPlatform,
		isImporting,
		error,
		onImport,
	};
}

export { useImportProduct, saveImportedProduct, consumeImportedProduct };
